import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { 
  Plus, 
  Trash2, 
  CalendarIcon,
  Fuel,
  Car,
  MoreHorizontal,
  Loader2,
  Wallet,
  Zap,
  Droplets,
  Home,
  Users,
  Wrench,
  Package,
  Phone,
  Truck,
  ShoppingCart,
  Building,
  Receipt,
  Printer,
  Download,
  Upload,
  Image,
  Sparkles,
  Eye,
  X
} from 'lucide-react';

const EXPENSE_CATEGORIES = {
  diesel: { label: 'ديزل / وقود', icon: Fuel, color: 'text-yellow-600 bg-yellow-50' },
  car_maintenance: { label: 'صيانة سيارة', icon: Car, color: 'text-blue-600 bg-blue-50' },
  electricity: { label: 'كهرباء', icon: Zap, color: 'text-amber-600 bg-amber-50' },
  water: { label: 'ماء', icon: Droplets, color: 'text-cyan-600 bg-cyan-50' },
  rent: { label: 'إيجار', icon: Home, color: 'text-purple-600 bg-purple-50' },
  salaries: { label: 'رواتب', icon: Users, color: 'text-green-600 bg-green-50' },
  equipment: { label: 'معدات / أدوات', icon: Wrench, color: 'text-orange-600 bg-orange-50' },
  raw_materials: { label: 'مواد خام', icon: Package, color: 'text-rose-600 bg-rose-50' },
  phone_internet: { label: 'هاتف / إنترنت', icon: Phone, color: 'text-indigo-600 bg-indigo-50' },
  delivery: { label: 'توصيل / نقل', icon: Truck, color: 'text-teal-600 bg-teal-50' },
  supplies: { label: 'مستلزمات', icon: ShoppingCart, color: 'text-pink-600 bg-pink-50' },
  taxes: { label: 'ضرائب / رسوم', icon: Building, color: 'text-red-600 bg-red-50' },
  insurance: { label: 'تأمين', icon: Receipt, color: 'text-sky-600 bg-sky-50' },
  other: { label: 'أخرى', icon: MoreHorizontal, color: 'text-gray-600 bg-gray-50' }
};

const ExpensesPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const today = new Date();
  const [startDate, setStartDate] = useState(startOfMonth(today));
  const [endDate, setEndDate] = useState(endOfMonth(today));
  
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    category: 'diesel',
    amount: '',
    description: '',
    invoice_number: '',
    invoice_image: null
  });
  
  // Image upload state
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [analyzingInvoice, setAnalyzingInvoice] = useState(false);
  const [viewImageDialog, setViewImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchExpenses();
  }, [startDate, endDate]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const start = format(startDate, 'yyyy-MM-dd');
      const end = format(endDate, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API_URL}/api/expenses?start_date=${start}&end_date=${end}`,
        { headers: getAuthHeader() }
      );
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('حدث خطأ في تحميل المصروفات');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setFormData({
      date: format(new Date(), 'yyyy-MM-dd'),
      category: 'diesel',
      amount: '',
      description: '',
      invoice_number: '',
      invoice_image: null
    });
    setImagePreview(null);
    setDialogOpen(true);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('يرجى اختيار صورة فقط');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
      return;
    }
    
    setUploadingImage(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setImagePreview(base64);
        setFormData(prev => ({ ...prev, invoice_image: base64 }));
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('فشل رفع الصورة');
      setUploadingImage(false);
    }
  };

  // Analyze invoice with AI
  const analyzeInvoiceWithAI = async () => {
    if (!imagePreview) {
      toast.error('يرجى رفع صورة الفاتورة أولاً');
      return;
    }
    
    setAnalyzingInvoice(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/expenses/analyze-invoice`,
        { image: imagePreview },
        { headers: getAuthHeader() }
      );
      
      const result = response.data;
      
      if (result.success) {
        // Update form with extracted data
        setFormData(prev => ({
          ...prev,
          amount: result.amount || prev.amount,
          description: result.description || prev.description,
          invoice_number: result.invoice_number || prev.invoice_number,
          date: result.date || prev.date,
          category: result.category || prev.category
        }));
        toast.success('تم استخراج بيانات الفاتورة بنجاح');
      } else {
        toast.warning('لم يتم العثور على بيانات واضحة في الفاتورة');
      }
    } catch (error) {
      console.error('Error analyzing invoice:', error);
      toast.error('فشل تحليل الفاتورة');
    } finally {
      setAnalyzingInvoice(false);
    }
  };

  // Remove image
  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, invoice_image: null }));
  };

  // View expense image
  const viewExpenseImage = (expense) => {
    if (expense.invoice_image) {
      setSelectedImage(expense.invoice_image);
      setViewImageDialog(true);
    }
  };

  const openDeleteDialog = (expense) => {
    setSelectedExpense(expense);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('الرجاء إدخال مبلغ صحيح');
      return;
    }

    setFormLoading(true);

    try {
      await axios.post(
        `${API_URL}/api/expenses`,
        {
          ...formData,
          amount: parseFloat(formData.amount)
        },
        { headers: getAuthHeader() }
      );
      toast.success('تم إضافة المصروف بنجاح');
      setDialogOpen(false);
      fetchExpenses();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('حدث خطأ في حفظ المصروف');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${API_URL}/api/expenses/${selectedExpense.id}`,
        { headers: getAuthHeader() }
      );
      toast.success('تم حذف المصروف بنجاح');
      setDeleteDialogOpen(false);
      fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('حدث خطأ في حذف المصروف');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount);
  };

  // Filter expenses by category
  const filteredExpenses = categoryFilter === 'all' 
    ? expenses 
    : expenses.filter(exp => exp.category === categoryFilter);

  // Calculate totals by category (from filtered)
  const totals = filteredExpenses.reduce((acc, exp) => {
    acc[exp.category] = (acc[exp.category] || 0) + exp.amount;
    acc.total = (acc.total || 0) + exp.amount;
    return acc;
  }, {});

  // Print expenses function
  const handlePrint = () => {
    const dateRange = `${format(startDate, 'yyyy/MM/dd')} - ${format(endDate, 'yyyy/MM/dd')}`;
    const filterLabel = categoryFilter === 'all' ? 'جميع الفئات' : EXPENSE_CATEGORIES[categoryFilter]?.label;
    
    let expensesHTML = filteredExpenses.map(exp => {
      const cat = EXPENSE_CATEGORIES[exp.category] || EXPENSE_CATEGORIES.other;
      return `
        <tr>
          <td style="border: 1px solid #d6d3d1; padding: 2mm;">${exp.date}</td>
          <td style="border: 1px solid #d6d3d1; padding: 2mm;">${cat.label}</td>
          <td style="border: 1px solid #d6d3d1; padding: 2mm;">${exp.description || '-'}</td>
          <td style="border: 1px solid #d6d3d1; padding: 2mm;">${exp.invoice_number || '-'}</td>
          <td style="border: 1px solid #d6d3d1; padding: 2mm; text-align: left; font-weight: bold;">${formatCurrency(exp.amount)} €</td>
        </tr>
      `;
    }).join('');

    let totalsHTML = Object.entries(totals)
      .filter(([key]) => key !== 'total')
      .map(([key, amount]) => {
        const cat = EXPENSE_CATEGORIES[key] || EXPENSE_CATEGORIES.other;
        return `<tr><td style="padding: 1mm 3mm;">${cat.label}:</td><td style="padding: 1mm 3mm; text-align: left;">${formatCurrency(amount)} €</td></tr>`;
      }).join('');

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>تقرير المصروفات</title>
        <style>
          @page { size: A4 portrait; margin: 10mm 15mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body { font-family: Arial, sans-serif; font-size: 10pt; direction: rtl; padding: 5mm; }
          table { border-collapse: collapse; width: 100%; }
        </style>
      </head>
      <body>
        <div style="text-align: center; margin-bottom: 6mm; border-bottom: 2px solid #d97706; padding-bottom: 4mm;">
          <h2 style="font-size: 18pt; font-weight: bold; margin: 0 0 2mm 0; color: #1c1917;">تقرير المصروفات</h2>
          <p style="font-size: 10pt; color: #57534e; margin: 0;">${dateRange}</p>
          <p style="font-size: 9pt; color: #78716c; margin: 1mm 0 0 0;">الفئة: ${filterLabel}</p>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 6mm;">
          <thead>
            <tr style="background: #f5f5f4;">
              <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">التاريخ</th>
              <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">الفئة</th>
              <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">الوصف</th>
              <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">رقم الفاتورة</th>
              <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: left;">المبلغ</th>
            </tr>
          </thead>
          <tbody>
            ${expensesHTML}
          </tbody>
        </table>
        
        <div style="margin-top: 5mm; padding: 3mm; background: #f5f5f4; border-radius: 2mm;">
          <h4 style="font-size: 11pt; margin: 0 0 2mm 0;">ملخص حسب الفئة:</h4>
          <table style="font-size: 9pt;">
            <tbody>
              ${totalsHTML}
              <tr style="border-top: 2px solid #d97706;">
                <td style="padding: 2mm 3mm; font-weight: bold; font-size: 11pt;">الإجمالي:</td>
                <td style="padding: 2mm 3mm; font-weight: bold; font-size: 11pt; text-align: left; color: #dc2626;">${formatCurrency(totals.total || 0)} €</td>
              </tr>
            </tbody>
          </table>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Download PDF function
  const handleDownloadPDF = () => {
    const dateRange = `${format(startDate, 'yyyy/MM/dd')} - ${format(endDate, 'yyyy/MM/dd')}`;
    const filterLabel = categoryFilter === 'all' ? 'جميع الفئات' : EXPENSE_CATEGORIES[categoryFilter]?.label;
    
    let expensesHTML = filteredExpenses.map(exp => {
      const cat = EXPENSE_CATEGORIES[exp.category] || EXPENSE_CATEGORIES.other;
      return `
        <tr>
          <td style="border: 1px solid #d6d3d1; padding: 2mm;">${exp.date}</td>
          <td style="border: 1px solid #d6d3d1; padding: 2mm;">${cat.label}</td>
          <td style="border: 1px solid #d6d3d1; padding: 2mm;">${exp.description || '-'}</td>
          <td style="border: 1px solid #d6d3d1; padding: 2mm;">${exp.invoice_number || '-'}</td>
          <td style="border: 1px solid #d6d3d1; padding: 2mm; text-align: left; font-weight: bold;">${formatCurrency(exp.amount)} €</td>
        </tr>
      `;
    }).join('');

    let totalsHTML = Object.entries(totals)
      .filter(([key]) => key !== 'total')
      .map(([key, amount]) => {
        const cat = EXPENSE_CATEGORIES[key] || EXPENSE_CATEGORIES.other;
        return `<tr><td style="padding: 1mm 3mm;">${cat.label}:</td><td style="padding: 1mm 3mm; text-align: left;">${formatCurrency(amount)} €</td></tr>`;
      }).join('');

    const pdfWindow = window.open('', '_blank');
    pdfWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>تقرير المصروفات</title>
        <style>
          @page { size: A4 portrait; margin: 10mm 15mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body { font-family: Arial, sans-serif; font-size: 10pt; direction: rtl; padding: 5mm; }
          table { border-collapse: collapse; width: 100%; }
        </style>
      </head>
      <body>
        <div style="text-align: center; margin-bottom: 6mm; border-bottom: 2px solid #d97706; padding-bottom: 4mm;">
          <h2 style="font-size: 18pt; font-weight: bold; margin: 0 0 2mm 0; color: #1c1917;">تقرير المصروفات</h2>
          <p style="font-size: 10pt; color: #57534e; margin: 0;">${dateRange}</p>
          <p style="font-size: 9pt; color: #78716c; margin: 1mm 0 0 0;">الفئة: ${filterLabel}</p>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 6mm;">
          <thead>
            <tr style="background: #f5f5f4;">
              <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">التاريخ</th>
              <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">الفئة</th>
              <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">الوصف</th>
              <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: right;">رقم الفاتورة</th>
              <th style="border: 1px solid #d6d3d1; padding: 2mm; text-align: left;">المبلغ</th>
            </tr>
          </thead>
          <tbody>
            ${expensesHTML}
          </tbody>
        </table>
        
        <div style="margin-top: 5mm; padding: 3mm; background: #f5f5f4; border-radius: 2mm;">
          <h4 style="font-size: 11pt; margin: 0 0 2mm 0;">ملخص حسب الفئة:</h4>
          <table style="font-size: 9pt;">
            <tbody>
              ${totalsHTML}
              <tr style="border-top: 2px solid #d97706;">
                <td style="padding: 2mm 3mm; font-weight: bold; font-size: 11pt;">الإجمالي:</td>
                <td style="padding: 2mm 3mm; font-weight: bold; font-size: 11pt; text-align: left; color: #dc2626;">${formatCurrency(totals.total || 0)} €</td>
              </tr>
            </tbody>
          </table>
        </div>
        <script>
          window.onload = function() {
            document.title = 'تقرير المصروفات - ${format(startDate, 'yyyy-MM-dd')}';
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    pdfWindow.document.close();
    toast.info('اختر "حفظ كـ PDF" من نافذة الطباعة');
  };

  return (
    <div data-testid="expenses-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-stone-900">المصروفات</h1>
          <p className="text-stone-500 mt-1">{expenses.length} مصروف</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={handlePrint}
            variant="outline"
            className="border-blue-300 text-blue-600 hover:bg-blue-50"
          >
            <Printer className="w-5 h-5 me-2" />
            طباعة
          </Button>
          <Button
            onClick={handleDownloadPDF}
            variant="outline"
            className="border-green-300 text-green-600 hover:bg-green-50"
          >
            <Download className="w-5 h-5 me-2" />
            PDF
          </Button>
          <Button
            data-testid="add-expense-btn"
            onClick={openAddDialog}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
          >
            <Plus className="w-5 h-5 me-2" />
            إضافة مصروف
          </Button>
        </div>
      </div>

      {/* Date Range and Filters */}
      <Card className="border-stone-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-600">من:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start text-right font-normal">
                    <CalendarIcon className="ms-2 h-4 w-4" />
                    {format(startDate, 'yyyy/MM/dd')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => date && setStartDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-600">إلى:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start text-right font-normal">
                    <CalendarIcon className="ms-2 h-4 w-4" />
                    {format(endDate, 'yyyy/MM/dd')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => date && setEndDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-600">الفئة:</span>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">جميع الفئات</SelectItem>
                  {Object.entries(EXPENSE_CATEGORIES).map(([key, cat]) => {
                    const Icon = cat.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${cat.color.split(' ')[0]}`} /> {cat.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {categoryFilter !== 'all' && (
              <Button variant="ghost" size="sm" onClick={() => setCategoryFilter('all')} className="text-stone-500">
                مسح الفلتر
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {Object.entries(EXPENSE_CATEGORIES).map(([key, cat]) => {
          const Icon = cat.icon;
          const amount = totals[key] || 0;
          if (amount === 0 && key !== 'other') return null; // Hide empty categories except 'other'
          return (
            <Card key={key} className={`border ${cat.color.replace('text-', 'border-').replace('bg-', '')} ${cat.color.split(' ')[1]}`}>
              <CardContent className="p-3 text-center">
                <Icon className={`w-5 h-5 mx-auto mb-1 ${cat.color.split(' ')[0]}`} />
                <p className={`text-lg font-bold font-numbers ${cat.color.split(' ')[0]}`}>
                  {formatCurrency(amount)}
                </p>
                <p className={`text-xs ${cat.color.split(' ')[0]}`}>{cat.label}</p>
              </CardContent>
            </Card>
          );
        })}
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-3 text-center">
            <Wallet className="w-5 h-5 mx-auto text-red-600 mb-1" />
            <p className="text-lg font-bold font-numbers text-red-700">
              {formatCurrency(totals.total || 0)}
            </p>
            <p className="text-xs text-red-600">الإجمالي</p>
          </CardContent>
        </Card>
      </div>

      {/* Expenses List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-10 h-10"></div>
        </div>
      ) : expenses.length === 0 ? (
        <div className="empty-state">
          <Wallet className="empty-state-icon" />
          <h3 className="empty-state-title">لا يوجد مصروفات</h3>
          <p className="empty-state-description">
            أضف مصروفاً جديداً لتتبع نفقاتك
          </p>
        </div>
      ) : (
        <>
          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {filteredExpenses.map((expense) => {
              const cat = EXPENSE_CATEGORIES[expense.category] || EXPENSE_CATEGORIES.other;
              const Icon = cat.icon;
              return (
                <Card key={expense.id} className="border-stone-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cat.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-bold text-stone-900">{cat.label}</p>
                          <p className="text-sm text-stone-500 font-numbers">{expense.date}</p>
                          {expense.invoice_number && (
                            <p className="text-xs text-blue-600">فاتورة: {expense.invoice_number}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-red-600 font-numbers">
                          {formatCurrency(expense.amount)}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(expense)}
                          className="text-stone-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {expense.description && (
                      <p className="text-sm text-stone-500 mt-2 bg-stone-50 rounded p-2">
                        {expense.description}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Desktop Table */}
          <Card className="hidden md:block border-stone-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="text-right font-bold">التاريخ</TableHead>
                  <TableHead className="text-right font-bold">الفئة</TableHead>
                  <TableHead className="text-right font-bold">المبلغ</TableHead>
                  <TableHead className="text-right font-bold">الوصف</TableHead>
                  <TableHead className="text-right font-bold">رقم الفاتورة</TableHead>
                  <TableHead className="text-right font-bold">إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.map((expense) => {
                  const cat = EXPENSE_CATEGORIES[expense.category] || EXPENSE_CATEGORIES.other;
                  return (
                    <TableRow key={expense.id} className="hover:bg-stone-50/50">
                      <TableCell className="font-numbers">{expense.date}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${cat.color}`}>
                          {cat.label}
                        </span>
                      </TableCell>
                      <TableCell className="font-bold text-red-600 font-numbers">
                        {formatCurrency(expense.amount)}
                      </TableCell>
                      <TableCell>{expense.description || '-'}</TableCell>
                      <TableCell className="font-numbers text-blue-600">{expense.invoice_number || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {expense.invoice_image && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => viewExpenseImage(expense)}
                              className="text-blue-500 hover:text-blue-700"
                              title="عرض صورة الفاتورة"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(expense)}
                            className="text-stone-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {/* Add Expense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>إضافة مصروف جديد</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>التاريخ</Label>
              <Input
                type="date"
                data-testid="expense-date-input"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>الفئة</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="expense-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {Object.entries(EXPENSE_CATEGORIES).map(([key, cat]) => {
                    const Icon = cat.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${cat.color.split(' ')[0]}`} /> {cat.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>المبلغ</Label>
              <Input
                type="number"
                data-testid="expense-amount-input"
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="أدخل المبلغ"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>الوصف (اختياري)</Label>
              <Input
                data-testid="expense-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="وصف المصروف..."
              />
            </div>

            <div className="space-y-2">
              <Label>رقم الفاتورة (اختياري)</Label>
              <Input
                data-testid="expense-invoice-input"
                value={formData.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                placeholder="رقم الفاتورة..."
              />
            </div>

            {/* Invoice Image Upload Section */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Image className="w-4 h-4 text-blue-600" />
                صورة الفاتورة (اختياري)
              </Label>
              
              {!imagePreview ? (
                <div className="border-2 border-dashed border-stone-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="invoice-image-input"
                  />
                  <label htmlFor="invoice-image-input" className="cursor-pointer">
                    <Upload className="w-8 h-8 mx-auto text-stone-400 mb-2" />
                    <p className="text-sm text-stone-500">اضغط لرفع صورة الفاتورة</p>
                    <p className="text-xs text-stone-400 mt-1">PNG, JPG حتى 5 ميجابايت</p>
                  </label>
                </div>
              ) : (
                <div className="relative border rounded-lg overflow-hidden">
                  <img 
                    src={imagePreview} 
                    alt="معاينة الفاتورة" 
                    className="w-full max-h-40 object-contain bg-stone-100"
                  />
                  <div className="absolute top-2 right-2 flex gap-1">
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="h-8 w-8 bg-white/90 hover:bg-white shadow"
                      onClick={removeImage}
                    >
                      <X className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                  
                  {/* AI Analysis Button */}
                  <div className="p-2 bg-stone-50 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                      onClick={analyzeInvoiceWithAI}
                      disabled={analyzingInvoice}
                    >
                      {analyzingInvoice ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          جاري التحليل...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          استخراج البيانات بالذكاء الاصطناعي
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                إلغاء
              </Button>
              <Button
                type="submit"
                data-testid="save-expense-btn"
                className="bg-amber-600 hover:bg-amber-700"
                disabled={formLoading}
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'إضافة'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>هل أنت متأكد من حذف هذا المصروف؟</AlertDialogTitle>
            <AlertDialogDescription>
              هذا الإجراء لا يمكن التراجع عنه.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Invoice Image Dialog */}
      <Dialog open={viewImageDialog} onOpenChange={setViewImageDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-blue-600" />
              صورة الفاتورة
            </DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="max-h-[70vh] overflow-auto">
              <img 
                src={selectedImage} 
                alt="صورة الفاتورة" 
                className="w-full object-contain rounded-lg"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewImageDialog(false)}>
              إغلاق
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
